//InfoLingua.js
import React, { useState, useEffect } from 'react';

import InfoButton from './components/InfoButton';
import { post_auth } from './auth';
import { serverUrl } from './ConfigHolder';
import FormExplain from './components/FormExplain';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';

import { useAuth } from './AuthProvider';
import ErrorDispatcher from './ErrorDispatcher';

const reqLinguistics = async (inputText, langLearn, langBase, expl_type, expl_style, logout, throwExp = false) => {
    
    const aiService = 'OpenAI'
    const data = {
        input_text: inputText,
        language: langLearn,
        langBase: langBase,
        // modelAI: 'gpt-4o-mini',
        // modelAI: 'gpt-4o',
        modelAI: 'default',
        aiService,
        explanation_type: expl_type,
        explanation_style: expl_style,
    };
    const uri = `${serverUrl}/explain`;
    try {
        const res = await post_auth(uri, data);
        return res.data;
    } catch (error) {
        console.error(error);
        if (throwExp) {
            throw error;
        }
        return null;
    }
};

const InfoLingua = ({ inputText, langLearn, langBase, expl_type, expl_style, style, 
                      infoCallBack, sentence, label, labelAlignment, verticalAlign, textOnly = false,
                      errorCallBack, icon }) => {
    const [prevText, setPrevText] = useState(null);
    const [explanation, setExplanation] = useState(null);
    const [explanationOpen, setExplanationOpen] = useState(false);
    const helpText = `Explanation of the ${expl_type}`;;
    const actionHelpText = `Explain ${label || expl_type}`;
    const {logout} = useAuth()
    const [error, setError] = useState(null);

    useEffect(() => {
        setExplanation(null);
    }, [langBase, inputText, langLearn, expl_type]);

    const functionalityInfo = (expl_type) => {
        return `+ Explanation of ${expl_type} will be displayed here.`;
    };

    const buildExplanationObj = (data) => {
        if (!data) return null;
        if (typeof data === 'string') {
            return {
                explanation: data,
                explanationType: expl_type,
                explanationStyle: expl_style,
            };
        }
        return {
            explanation: data.explanation,
            explanationType: data.explanationType ?? expl_type,
            explanationStyle: data.explanationStyle ?? expl_style,
            model: data.model,
            service_name: data.service_name,
        };
    };

    const explanationOnClose = () => {
        setExplanationOpen(false);
    };

    const getExplanation = (sentence) => {
        if (sentence?.phraseVal?.explanations) {
            return sentence.phraseVal.explanations.find((expl) => 
            expl.explanationStyle === expl_style && expl.explanationType === expl_type
            );
        } else {
            return undefined;
        }
    };

    const onClick = async () => {
        const trimmedText = inputText.trim();
        if (!trimmedText) {
            setExplanation(buildExplanationObj(functionalityInfo(expl_type)));
            setExplanationOpen(true);
            return;
        }
        if (inputText === prevText && explanation) {
            setExplanationOpen(true);
            return;
        }
        setExplanation(buildExplanationObj('### Please, Wait'));
        setExplanationOpen(true);

        const expl_stored = getExplanation(sentence);
        if (expl_stored) {
            setExplanation(buildExplanationObj(expl_stored));
        } else {
            let res;
            try {
                res = await reqLinguistics(inputText, langLearn, langBase, expl_type, expl_style, logout, true);
            } catch (error) {
                console.error('Error fetching explanation:', error);
                if (errorCallBack) { errorCallBack(error); } 
                else {
                    setError(error);
                }
                res = null;
            }
            if (res?.explanation) {
                const explanationObj = buildExplanationObj(res);
                setExplanation(explanationObj);
                if (infoCallBack) {
                    infoCallBack(explanationObj, expl_type, expl_style, sentence);
                }
            } else {
                setExplanationOpen(false);
                setExplanation(null);
            }
            setPrevText(inputText);
        }
    };

    return (
        <Box>
            { !textOnly ? ( <InfoButton
                sx={style}
                onClick={onClick}
                helpText={helpText}
                label={label}
                labelAlignment={labelAlignment}
                verticalAlign = {verticalAlign}
            /> ) : icon ? (
                <Tooltip title={actionHelpText} arrow>
                    <IconButton
                        aria-label={actionHelpText}
                        onClick={onClick}
                        size="small"
                        sx={{
                            bgcolor: 'rgba(25, 118, 210, 0.08)',
                            border: '1px solid',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            height: 32,
                            overflow: 'visible',
                            position: 'relative',
                            width: 32,
                            '&:hover': {
                                bgcolor: 'rgba(25, 118, 210, 0.16)',
                                borderColor: 'primary.dark',
                                color: 'primary.dark',
                            },
                            '& svg': {
                                fontSize: '1rem',
                            },
                            ...style,
                        }}
                    >
                        {icon}
                        <Box
                            component="span"
                            sx={{
                                alignItems: 'center',
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'primary.main',
                                borderRadius: '50%',
                                color: 'primary.main',
                                display: 'inline-flex',
                                fontSize: '0.55rem',
                                fontWeight: 700,
                                height: 13,
                                justifyContent: 'center',
                                lineHeight: 1,
                                position: 'absolute',
                                right: -5,
                                top: -5,
                                width: 13,
                            }}
                        >
                            ?
                        </Box>
                    </IconButton>
                </Tooltip>
            ) : (
                <Typography
                    onClick={onClick}
                    // variant="h6"
                    sx={{
                        mr: 2,
                        p: 0,
                        color: 'blue',
                        textDecoration: 'underline', // Always underlined
                        cursor: 'pointer', // Indicates clickable text
                    }}
                    >
                    {label}
                </Typography>
             )
             }
            <FormExplain
                isOpen={explanationOpen}
                onClose={explanationOnClose}
                markdownText={explanation?.explanation}
                explanationType={label}
                model={explanation?.model}
                serviceName={explanation?.service_name}
            />
        {error && <ErrorDispatcher error={error} 
            showDialog={true}
            navigateToLogin={true}
        
        />}
        </Box>
    );
};

export {InfoLingua, reqLinguistics} ;
